/**
 * @fileoverview Utilitários para tratamento de erros HTTP
 * @module webservice/utils/errorHandler
 * @description Funções centralizadas para tratamento de erros de API
 */

/**
 * Enriquece erros HTTP com informações úteis e padronizadas
 *
 * Esta função centraliza o tratamento de erros das chamadas de API,
 * evitando duplicação de código entre diferentes serviços.
 *
 * @param {Error} error - Erro original capturado no catch
 * @param {string} context - Contexto da operação (ex: 'verificarAtualizacoesReservas')
 * @returns {Error} Erro enriquecido com informações úteis
 *
 * @example
 * try {
 *   const response = await http.get('/api/endpoint');
 * } catch (error) {
 *   throw handleApiError(error, 'meuServico');
 * }
 */
export function handleApiError(error, context = 'API') {
  // Requisição cancelada (por navegação ou timeout)
  if (error.__CANCEL__ || error.message === 'Request has been canceled by the user') {
    error.message = `Requisição cancelada em ${context}`
    error.isCanceled = true

    // Log específico para cancelamento
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${context}] Requisição cancelada - Comportamento esperado`)
    }

    return error
  }

  // Enriquece o erro com informações úteis
  if (error.response) {
    // Erro HTTP da API (4xx, 5xx)
    const status = error.response.status
    const data = error.response.data || {}

    if (status === 401) {
      // Não autorizado - sessão pode ter expirado
      error.message = 'Sessão expirada. Faça login novamente.'
      error.needsReauth = true
    } else if (status === 422) {
      // Erro de validação
      const validationErrors = data.errors || {}
      error.message = data.message || 'Erro de validação nos parâmetros'
      error.validationErrors = validationErrors
    } else if (status === 500) {
      // Erro interno do servidor
      error.message = data.message || `Erro interno do servidor em ${context}`
    } else {
      error.message = data.message || `Erro HTTP ${status} em ${context}`
    }
  } else if (error.code === 'ECONNABORTED') {
    // Timeout da requisição
    error.message = `Timeout em ${context}. A sincronização com o ERP está demorando mais que o esperado.`
    error.isTimeout = true

    // Log específico para timeout
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[${context}] Timeout - considere verificar performance da procedure no ERP`)
    }
  } else if (error.code === 'ERR_NETWORK' || !error.response) {
    // Erro de rede
    error.message = `Erro de conexão em ${context}. Verifique sua internet.`
    error.isNetworkError = true
  } else {
    // Erro desconhecido
    error.message = error.message || `Erro desconhecido em ${context}`
  }

  // Log em desenvolvimento
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Erro:`, error)
  }

  return error
}

/**
 * Valida resposta da API com campos esperados
 *
 * @param {Object} response - Resposta da API
 * @param {Array<string>} requiredFields - Campos obrigatórios
 * @param {string} context - Contexto da validação
 * @throws {Error} Se resposta for inválida
 *
 * @example
 * validateApiResponse(response, ['tem_atualizacao', 'hash_atual'], 'verificarAtualizacoes');
 */
export function validateApiResponse(response, requiredFields = [], context = 'API') {
  if (!response || !response.data) {
    throw new Error(`${context}: Resposta da API está vazia`)
  }

  // Validação dos campos obrigatórios (apenas em dev)
  if (process.env.NODE_ENV === 'development' && requiredFields.length > 0) {
    requiredFields.forEach(field => {
      if (!(field in response.data)) {
        console.warn(`${context}: Campo '${field}' ausente na resposta`)
      }
    })
  }
}

/**
 * Valida hash MD5
 *
 * @param {string} hash - Hash a ser validado
 * @param {string} fieldName - Nome do campo (para mensagens de erro)
 * @param {boolean} required - Se o hash é obrigatório
 * @throws {Error} Se hash for inválido
 */
export function validateHash(hash, fieldName = 'hash', required = false) {
  if (!hash) {
    if (required) {
      throw new Error(`${fieldName} é obrigatório`)
    }
    return
  }

  if (typeof hash !== 'string' || hash.length !== 32) {
    throw new Error(`${fieldName} deve ser uma string de 32 caracteres (MD5)`)
  }
}

export default {
  handleApiError,
  validateApiResponse,
  validateHash
}
